// src/pages/GamesPage.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Subpage from "../components/Subpage";
import AnimatedButton from "../components/AnimatedButton";
import { slugOrId } from "../utils/slugOrId";
import { API_BASE, mediaUrl } from "../utils/apiBase";

function Banner({ post, isLegacy }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  /* image drifts slowly while title/CTA stay locked in the overlay */
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  const teaser = post.TeaserImage?.url || post.TeaserImage?.attributes?.url;
  const thumb =
    teaser ||
    post.Sections?.flatMap((s) => s.Media || [])
      .map((m) => m.url || m.attributes?.url)
      .filter(Boolean)[0];

  const heightClass = isLegacy ? "h-80" : "h-[544px]";

  return (
    <div
      ref={ref}
      className={`relative w-screen left-1/2 -translate-x-1/2 overflow-hidden ${heightClass}`}
    >
      {thumb && (
        <motion.img
          src={mediaUrl(thumb)}
          alt={post.Title}
          style={{ y }}
          className="absolute left-0 top-[-12%] w-full h-[124%] object-cover will-change-transform"
        />
      )}

      {isLegacy && (
        <div className="absolute top-0 left-[-57px] w-64 h-32 overflow-visible z-20">
          <img
            src="/images/legacy_banner.png"
            alt="Legacy ribbon"
            className="w-full h-full object-contain"
          />
          <span
            className="
            absolute
            top-7
            left-20
            items-center justify-center
            text-black uppercase font-bold
            transform -rotate-45
            "
          >
            Legacy
          </span>
        </div>
      )}

      {/* title + CTA stay fixed over the moving image */}
      <div className="absolute inset-0 z-10 bg-black bg-opacity-10 flex flex-col items-center justify-center text-center px-4">
        <h2 className="text-3xl md:text-5xl font-bold mb-2 drop-shadow-md text-white">
          {post.Title}
        </h2>

        {post.TitleDescription && (
          <p className="text-md md:text-lg mb-4 max-w-xl text-white drop-shadow-md">
            {post.TitleDescription}
          </p>
        )}

        <Link to={`/games/${slugOrId(post)}`}>
          <AnimatedButton
            text="Discover More"
            href={`/games/${slugOrId(post)}`}
            className="bg-white text-black"
          />
        </Link>
      </div>
    </div>
  );
}

export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [showLegacy, setShowLegacy] = useState(false);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    axios
      .get(
        `${API_BASE}/games?populate[Sections][populate]=Media&populate=TeaserImage&sort=PublishDate:desc`
      )
      .then((res) => setGames(res.data.data))
      .catch((err) => console.error("Error fetching games:", err));
  }, []);

  const nonLegacy = games.filter((g) => !g.Legacy);
  const legacy = games.filter((g) => g.Legacy);

  return (
    <Subpage
      title="OUR GAMES"
      content="Explore our original titles and legacy co-creations from a different era."
    >
      {nonLegacy.map((g) => (
        <Banner key={g.documentId} post={g} isLegacy={false} />
      ))}

      {!showLegacy && legacy.length > 0 && (
        <div className="flex justify-center mt-12 mb-12">
          <button
            ref={loadMoreRef}
            onClick={() => {
              const scrollY = window.scrollY;
              const offsetTop =
                loadMoreRef.current?.getBoundingClientRect().top || 0;
              const absoluteY = scrollY + offsetTop;

              setShowLegacy(true);

              setTimeout(() => {
                window.scrollTo({ top: absoluteY, behavior: "instant" });
                const firstLegacy = document.getElementById("legacy-anchor");
                if (firstLegacy) {
                  firstLegacy.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }, 0);
            }}
            className="border-2 border-gray-900 text-gray-900 bg-white px-8 py-4 uppercase font-semibold hover:bg-gray-900 hover:text-white transition"
          >
            Load Legacy Games
          </button>
        </div>
      )}

      {showLegacy &&
        legacy.map((g, i) => (
          <div key={g.documentId} id={i === 0 ? "legacy-anchor" : undefined}>
            <Banner post={g} isLegacy={true} />
          </div>
        ))}

      <div className="h-20" />
    </Subpage>
  );
}
